"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Calendar, ShoppingBag, ShieldCheck, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
export default function Consultation() {
  const shouldReduceMotion = useReducedMotion();
  const benefits = [{
    text: "Book a direct consultation with me — no account managers or outsourced junior staff."
  }, {
    text: "Clear technical and content roadmaps focused on revenue, not vanity impressions."
  }, {
    text: "Honest feasibility analysis before you commit to an ongoing partnership."
  }];
  return <section id="closingconsultationcta" data-nav="light" className="relative overflow-hidden bg-background py-20 lg:py-28 border-t border-border">
      {/* Decorative background glow accents */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 right-0 size-96 rounded-full bg-secondary/60 blur-3xl opacity-50" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 left-0 size-96 rounded-full bg-secondary/40 blur-3xl opacity-40" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="mx-auto max-w-4xl">
          <Card className="border border-border/80 bg-card/60 backdrop-blur-xs shadow-xl p-8 sm:p-12 lg:p-14 rounded-2xl">
            <CardContent className="p-0 flex flex-col items-center text-center space-y-8">
              {/* Top pill badge */}
              <Badge variant="secondary" className="px-3.5 py-1 text-xs font-semibold uppercase tracking-wider text-primary border border-secondary">
                Book Your Free Consultation
              </Badge>

              {/* Headings */}
              <div className="space-y-4 max-w-2xl">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
                  Ready to grow your search revenue?
                </h2>
                <p className="text-base sm:text-lg text-muted-foreground leading-relaxed font-default">
                  Book a free 30-minute consultation with me to review your store and map out a bespoke SEO roadmap tailored to your revenue targets.
                </p>
              </div>

              {/* Consultant key benefits list */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full text-left pt-2 pb-2">
                {benefits.map((benefit, i) => <div key={i} data-index={i} className="flex items-start gap-3 p-3.5 rounded-xl bg-background/80 border border-border/60 shadow-2xs">
                    <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                    <span className="text-xs sm:text-sm text-foreground leading-snug">
                      {benefit.text}
                    </span>
                  </div>)}
              </div>

              {/* Main CTAs */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full pt-4">
                <Button asChild size="lg" className="w-full sm:w-auto px-8 py-6 text-base font-semibold shadow-md transition-[color,background-color,box-shadow]">
                  <Link to="/ContactPage">
                    <SafeHydrate><Calendar className="size-5 mr-2" /></SafeHydrate>
                    Book a Free SEO Consultation
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="w-full sm:w-auto px-6 py-6 text-base font-medium border-border hover:bg-secondary/40 text-foreground transition-[color,background-color,border-color]">
                  <Link to="/EcommerceSeoPage" className="inline-flex items-center">
                    <ShoppingBag className="size-4 mr-2 text-primary" />
                    Ecommerce SEO
                    <ArrowRight className="size-4 ml-2" />
                  </Link>
                </Button>
              </div>

              {/* Trust disclaimer */}
              <div className="flex items-center gap-2 pt-2 text-xs text-muted-foreground">
                <ShieldCheck className="size-4 text-primary" />
                <span>30-minute discovery session • No pressure or obligation</span>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>;
}